/** @module @liquidefy/code-editor/document/document @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  EditorEvent,
  EditorResult,
  DocumentId,
  LanguageId,
  DocumentEncoding,
  LineEnding,
  ByteOrderMark,
  DocumentState,
  DocumentSnapshot,
  Timestamp,
} from '../code-editor-types.js';

import {
  ok,
  err,
  now,
  generateId,
  asDocumentId,
  asLanguageId,
  lineCount,
  positionToOffset,
  offsetToPosition,
} from '../code-editor.js';

// ═══ §2 DOCUMENT CREATION & LIFECYCLE — create, close, reopen, clone ═══

export function createDocument(
  content: string,
  language: string,
  encoding: DocumentEncoding = 'utf-8',
): EditorResult<DocumentState> {
  const id = asDocumentId(generateId('doc'));
  const detectedEnding = detectLineEnding(content);
  const normalizedContent = normalizeLineEndings(content, detectedEnding);
  const bom = detectBOM(content);
  const cleaned = bom !== 'none' ? stripBOM(content) : normalizedContent;

  const state: DocumentState = {
    id,
    content: cleaned,
    language: asLanguageId(language),
    version: 1,
    encoding,
    lineEnding: detectedEnding,
    byteOrderMark: bom,
    lineCount: lineCount(cleaned),
    charCount: cleaned.length,
    isDirty: false,
    isReadOnly: false,
    eol: detectedEnding === 'crlf' ? '\r\n' : detectedEnding === 'cr' ? '\r' : '\n',
  };

  const event: EditorEvent = {
    kind: 'created',
    domain: 'document',
    timestamp: now(),
    payload: { documentId: id, language, encoding },
  };

  return ok(state, 'DOCUMENT_CREATED', 'Document created', [event]);
}

export function closeDocument(state: DocumentState): EditorResult<DocumentSnapshot> {
  const snapshot: DocumentSnapshot = {
    id: generateId('snap'),
    documentId: state.id,
    version: state.version,
    content: state.content,
    timestamp: now(),
  };

  const event: EditorEvent = {
    kind: 'closed',
    domain: 'document',
    timestamp: now(),
    payload: { documentId: state.id, version: state.version },
  };

  return ok(snapshot, 'DOCUMENT_CLOSED', 'Document closed', [event]);
}

export function reopenDocument(snapshot: DocumentSnapshot): EditorResult<DocumentState> {
  const detectedEnding = detectLineEnding(snapshot.content);

  const state: DocumentState = {
    id: snapshot.documentId,
    content: snapshot.content,
    language: asLanguageId('plaintext'),
    version: snapshot.version,
    encoding: 'utf-8',
    lineEnding: detectedEnding,
    byteOrderMark: detectBOM(snapshot.content),
    lineCount: lineCount(snapshot.content),
    charCount: snapshot.content.length,
    isDirty: false,
    isReadOnly: false,
    eol: detectedEnding === 'crlf' ? '\r\n' : detectedEnding === 'cr' ? '\r' : '\n',
  };

  const event: EditorEvent = {
    kind: 'opened',
    domain: 'document',
    timestamp: now(),
    payload: { documentId: snapshot.documentId, version: snapshot.version },
  };

  return ok(state, 'DOCUMENT_REOPENED', 'Document reopened from snapshot', [event]);
}

export function cloneDocument(state: DocumentState, newId?: DocumentId): EditorResult<DocumentState> {
  const id = newId ?? asDocumentId(generateId('doc'));

  const cloned: DocumentState = {
    ...state,
    id,
    version: 1,
    isDirty: false,
  };

  const event: EditorEvent = {
    kind: 'created',
    domain: 'document',
    timestamp: now(),
    payload: { documentId: id, clonedFrom: state.id },
  };

  return ok(cloned, 'DOCUMENT_CLONED', 'Document cloned', [event]);
}

// ═══ §3 CONTENT MANIPULATION — insert, delete, replace, transpose, indent, etc. ═══

export function insertAt(state: DocumentState, position: Position, text: string): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');
  if (text.length === 0) return ok(state, 'NOOP', 'Nothing to insert');

  const offset = positionToOffset(state.content, position);
  const newContent = state.content.slice(0, offset) + text + state.content.slice(offset);

  const updated = updateDocumentContent(state, newContent);
  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'insert', position, textLength: text.length },
  };

  return ok(updated, 'CONTENT_INSERTED', 'Text inserted', [event]);
}

export function deleteRange(state: DocumentState, range: Range): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const startOffset = positionToOffset(state.content, range.start);
  const endOffset = positionToOffset(state.content, range.end);

  if (startOffset === endOffset) return ok(state, 'NOOP', 'Empty range');

  const newContent = state.content.slice(0, startOffset) + state.content.slice(endOffset);
  const updated = updateDocumentContent(state, newContent);

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'delete', range, deletedLength: endOffset - startOffset },
  };

  return ok(updated, 'CONTENT_DELETED', 'Range deleted', [event]);
}

export function replaceRange(state: DocumentState, range: Range, text: string): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const startOffset = positionToOffset(state.content, range.start);
  const endOffset = positionToOffset(state.content, range.end);
  const newContent = state.content.slice(0, startOffset) + text + state.content.slice(endOffset);

  const updated = updateDocumentContent(state, newContent);

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'replace', range, newTextLength: text.length },
  };

  return ok(updated, 'CONTENT_REPLACED', 'Range replaced', [event]);
}

export function transpose(state: DocumentState, position: Position): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const offset = positionToOffset(state.content, position);
  if (offset <= 0 || offset >= state.content.length) {
    return err('OUT_OF_BOUNDS', 'Cannot transpose at boundary');
  }

  const chars = state.content.split('');
  const temp = chars[offset - 1]!;
  chars[offset - 1] = chars[offset]!;
  chars[offset] = temp;

  const updated = updateDocumentContent(state, chars.join(''));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'transpose', position },
  };

  return ok(updated, 'CONTENT_TRANSPOSED', 'Characters transposed', [event]);
}

export function indentLines(
  state: DocumentState,
  lines: number[],
  tabSize: number,
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  const indent = ' '.repeat(tabSize);

  for (const lineNum of lines) {
    if (lineNum >= 0 && lineNum < allLines.length) {
      allLines[lineNum] = indent + allLines[lineNum];
    }
  }

  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'indent', lines, tabSize },
  };

  return ok(updated, 'LINES_INDENTED', 'Lines indented', [event]);
}

export function dedentLines(
  state: DocumentState,
  lines: number[],
  tabSize: number,
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);

  for (const lineNum of lines) {
    if (lineNum >= 0 && lineNum < allLines.length) {
      const line = allLines[lineNum]!;
      let removed = 0;
      for (let i = 0; i < line.length && removed < tabSize; i++) {
        if (line[i] === ' ') {
          removed++;
        } else if (line[i] === '\t') {
          removed = tabSize;
        } else {
          break;
        }
      }
      allLines[lineNum] = line!.slice(removed);
    }
  }

  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'dedent', lines, tabSize },
  };

  return ok(updated, 'LINES_DEDENTED', 'Lines dedented', [event]);
}

export function toggleComment(
  state: DocumentState,
  lines: number[],
  commentRule: { lineComment?: string; blockComment?: readonly [string, string] },
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');
  if (!commentRule.lineComment && !commentRule.blockComment) {
    return err('NO_COMMENT_RULE', 'No comment rule provided');
  }

  const allLines = splitLines(state.content);
  const marker = commentRule.lineComment ?? '';

  if (!marker) {
    return err('NO_LINE_COMMENT', 'Line comment rule required for toggle');
  }

  const validLines = lines.filter(l => l >= 0 && l < allLines.length);
  if (validLines.length === 0) return ok(state, 'NOOP', 'No valid lines');

  const allCommented = validLines.every(l => {
    const trimmed = allLines[l]!.trimStart();
    return trimmed.startsWith(marker);
  });

  for (const lineNum of validLines) {
    if (allCommented) {
      const idx = allLines[lineNum]!.indexOf(marker);
      if (idx >= 0) {
        const after = allLines[lineNum]!.slice(idx + marker.length);
        const stripped = after.startsWith(' ') ? after.slice(1) : after;
        allLines[lineNum] = allLines[lineNum]!.slice(0, idx) + stripped;
      }
    } else {
      const firstNonWs = allLines[lineNum]!.search(/\S/);
      if (firstNonWs >= 0) {
        allLines[lineNum] =
          allLines[lineNum]!.slice(0, firstNonWs) + marker + ' ' + allLines[lineNum]!.slice(firstNonWs);
      } else {
        allLines[lineNum] = marker + ' ' + allLines[lineNum]!;
      }
    }
  }

  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'commentToggle', lines: validLines, commented: !allCommented },
  };

  return ok(updated, 'COMMENTS_TOGGLED', allCommented ? 'Comments removed' : 'Comments added', [event]);
}

export function duplicateLine(state: DocumentState, lineNumber: number): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  if (lineNumber < 0 || lineNumber >= allLines.length) {
    return err('OUT_OF_BOUNDS', `Line ${lineNumber} out of bounds`);
  }

  allLines.splice(lineNumber + 1, 0, allLines[lineNumber]!);
  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'duplicateLine', lineNumber },
  };

  return ok(updated, 'LINE_DUPLICATED', 'Line duplicated', [event]);
}

export function moveLinesUp(
  state: DocumentState,
  startLine: number,
  endLine: number,
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  if (startLine <= 0 || endLine >= allLines.length || startLine > endLine) {
    return err('OUT_OF_BOUNDS', 'Cannot move lines up');
  }

  const moved = allLines.splice(startLine, endLine - startLine + 1);
  allLines.splice(startLine - 1, 0, ...moved);
  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'moved',
    domain: 'document',
    timestamp: now(),
    payload: { direction: 'up', startLine, endLine },
  };

  return ok(updated, 'LINES_MOVED_UP', 'Lines moved up', [event]);
}

export function moveLinesDown(
  state: DocumentState,
  startLine: number,
  endLine: number,
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  if (startLine < 0 || endLine >= allLines.length - 1 || startLine > endLine) {
    return err('OUT_OF_BOUNDS', 'Cannot move lines down');
  }

  const moved = allLines.splice(startLine, endLine - startLine + 1);
  allLines.splice(startLine + 1, 0, ...moved);
  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'moved',
    domain: 'document',
    timestamp: now(),
    payload: { direction: 'down', startLine, endLine },
  };

  return ok(updated, 'LINES_MOVED_DOWN', 'Lines moved down', [event]);
}

export function joinLinesRange(
  state: DocumentState,
  startLine: number,
  endLine: number,
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  const clampedStart = Math.max(0, startLine);
  const clampedEnd = Math.min(allLines.length - 1, endLine);

  if (clampedStart >= clampedEnd) return ok(state, 'NOOP', 'Nothing to join');

  const joined = allLines.slice(clampedStart, clampedEnd + 1).map(l => l.trimEnd()).join(' ');
  allLines.splice(clampedStart, clampedEnd - clampedStart + 1, joined);

  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'joinLines', startLine: clampedStart, endLine: clampedEnd },
  };

  return ok(updated, 'LINES_JOINED', 'Lines joined', [event]);
}

export function sortLines(
  state: DocumentState,
  startLine: number,
  endLine: number,
  descending: boolean = false,
): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  const clampedStart = Math.max(0, startLine);
  const clampedEnd = Math.min(allLines.length - 1, endLine);

  if (clampedStart >= clampedEnd) return ok(state, 'NOOP', 'Nothing to sort');

  const segment = allLines.slice(clampedStart, clampedEnd + 1);
  segment.sort((a, b) => {
    const cmp = a.localeCompare(b);
    return descending ? -cmp : cmp;
  });
  allLines.splice(clampedStart, clampedEnd - clampedStart + 1, ...segment);

  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'sortLines', startLine: clampedStart, endLine: clampedEnd, descending },
  };

  return ok(updated, 'LINES_SORTED', 'Lines sorted', [event]);
}

export function trimTrailingWhitespace(state: DocumentState): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  const allLines = splitLines(state.content);
  let changed = false;

  for (let i = 0; i < allLines.length; i++) {
    const trimmed = allLines[i]!.replace(/\s+$/, '');
    if (trimmed !== allLines[i]) {
      allLines[i] = trimmed;
      changed = true;
    }
  }

  if (!changed) return ok(state, 'NOOP', 'No trailing whitespace found');

  const updated = updateDocumentContent(state, joinLines(allLines, state.eol));

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'trimTrailingWhitespace' },
  };

  return ok(updated, 'WHITESPACE_TRIMMED', 'Trailing whitespace trimmed', [event]);
}

export function insertFinalNewline(state: DocumentState): EditorResult<DocumentState> {
  if (state.isReadOnly) return err('READONLY', 'Document is read-only');

  if (state.content.length === 0 || state.content.endsWith('\n') || state.content.endsWith('\r')) {
    return ok(state, 'NOOP', 'Final newline already present');
  }

  const newContent = state.content + state.eol;
  const updated = updateDocumentContent(state, newContent);

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'insertFinalNewline' },
  };

  return ok(updated, 'NEWLINE_INSERTED', 'Final newline inserted', [event]);
}

// ═══ §4 LINE OPERATIONS — query lines, lengths, whitespace ═══

export function getLine(state: DocumentState, lineNumber: number): EditorResult<string> {
  const allLines = splitLines(state.content);
  if (lineNumber < 0 || lineNumber >= allLines.length) {
    return err('OUT_OF_BOUNDS', `Line ${lineNumber} out of bounds (0..${allLines.length - 1})`);
  }
  return ok(allLines[lineNumber]!, 'OK', 'Line retrieved');
}

export function getLines(state: DocumentState, startLine: number, endLine: number): EditorResult<string[]> {
  const allLines = splitLines(state.content);
  const clampedStart = Math.max(0, startLine);
  const clampedEnd = Math.min(allLines.length - 1, endLine);

  if (clampedStart > clampedEnd) return ok([], 'OK', 'Empty range');

  return ok(allLines.slice(clampedStart, clampedEnd + 1), 'OK', 'Lines retrieved');
}

export function getLineLength(state: DocumentState, lineNumber: number): EditorResult<number> {
  const allLines = splitLines(state.content);
  if (lineNumber < 0 || lineNumber >= allLines.length) {
    return err('OUT_OF_BOUNDS', `Line ${lineNumber} out of bounds`);
  }
  return ok(allLines[lineNumber]!.length, 'OK', 'Line length retrieved');
}

export function getLongestLine(state: DocumentState): EditorResult<{ lineNumber: number; length: number }> {
  const allLines = splitLines(state.content);
  let maxLen = 0;
  let maxLine = 0;

  for (let i = 0; i < allLines.length; i++) {
    if (allLines[i]!.length > maxLen) {
      maxLen = allLines[i]!.length;
      maxLine = i;
    }
  }

  return ok({ lineNumber: maxLine, length: maxLen }, 'OK', 'Longest line found');
}

export function getFirstNonWhitespace(state: DocumentState, lineNumber: number): EditorResult<number> {
  const allLines = splitLines(state.content);
  if (lineNumber < 0 || lineNumber >= allLines.length) {
    return err('OUT_OF_BOUNDS', `Line ${lineNumber} out of bounds`);
  }

  const idx = allLines[lineNumber]!.search(/\S/);
  return ok(idx < 0 ? allLines[lineNumber]!.length : idx, 'OK', 'First non-whitespace index');
}

export function isLineEmpty(state: DocumentState, lineNumber: number): EditorResult<boolean> {
  const allLines = splitLines(state.content);
  if (lineNumber < 0 || lineNumber >= allLines.length) {
    return err('OUT_OF_BOUNDS', `Line ${lineNumber} out of bounds`);
  }
  return ok(allLines[lineNumber]!.length === 0, 'OK', 'Line empty check');
}

export function isLineWhitespace(state: DocumentState, lineNumber: number): EditorResult<boolean> {
  const allLines = splitLines(state.content);
  if (lineNumber < 0 || lineNumber >= allLines.length) {
    return err('OUT_OF_BOUNDS', `Line ${lineNumber} out of bounds`);
  }
  return ok(allLines[lineNumber]!.trim().length === 0, 'OK', 'Line whitespace check');
}

// ═══ §5 ENCODING — convert encoding, line endings, BOM ═══

export function convertEncoding(
  state: DocumentState,
  target: DocumentEncoding,
): EditorResult<DocumentState> {
  if (state.encoding === target) return ok(state, 'NOOP', 'Already in target encoding');

  const updated: DocumentState = {
    ...state,
    encoding: target,
    version: state.version + 1,
    isDirty: true,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'encoding', from: state.encoding, to: target },
  };

  return ok(updated, 'ENCODING_CONVERTED', `Encoding changed to ${target}`, [event]);
}

export function convertLineEndings(
  state: DocumentState,
  target: LineEnding,
): EditorResult<DocumentState> {
  if (state.lineEnding === target) return ok(state, 'NOOP', 'Already using target line endings');

  const targetEol = target === 'crlf' ? '\r\n' : target === 'cr' ? '\r' : '\n';
  const normalized = normalizeLineEndings(state.content, target);

  const updated: DocumentState = {
    ...state,
    content: normalized,
    lineEnding: target,
    eol: targetEol,
    version: state.version + 1,
    isDirty: true,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { kind: 'lineEndings', from: state.lineEnding, to: target },
  };

  return ok(updated, 'LINE_ENDINGS_CONVERTED', `Line endings changed to ${target}`, [event]);
}

export function detectBOM(content: string): ByteOrderMark {
  if (content.charCodeAt(0) === 0xfeff) return 'utf-8';
  if (content.charCodeAt(0) === 0xfffe) return 'utf-16le';
  if (content.charCodeAt(0) === 0xfeff && content.charCodeAt(1) === 0x0000) return 'utf-16be';
  return 'none';
}

export function stripBOM(content: string): string {
  if (
    content.charCodeAt(0) === 0xfeff ||
    content.charCodeAt(0) === 0xfffe
  ) {
    return content.slice(1);
  }
  return content;
}

export function addBOM(content: string, encoding: ByteOrderMark): string {
  if (encoding === 'none') return content;
  const stripped = stripBOM(content);

  if (encoding === 'utf-8') return '\ufeff' + stripped;
  if (encoding === 'utf-16le') return '\ufffe' + stripped;
  if (encoding === 'utf-16be') return '\ufeff' + stripped;

  return stripped;
}

// ═══ §6 DOCUMENT ANALYSIS — word count, char count, statistics, checksum ═══

export function countWords(content: string): number {
  if (content.trim().length === 0) return 0;
  const words = content.trim().split(/\s+/);
  return words.length;
}

export function countCharacters(content: string, includeWhitespace: boolean = true): number {
  if (includeWhitespace) return content.length;
  return content.replace(/\s/g, '').length;
}

export function computeStatistics(state: DocumentState): EditorResult<{
  lines: number;
  words: number;
  chars: number;
  byteSize: number;
  avgLineLength: number;
}> {
  const words = countWords(state.content);
  const chars = state.content.length;
  const byteSize = new TextEncoder().encode(state.content).length;
  const lines = state.lineCount;
  const avgLineLength = lines > 0 ? Math.round(chars / lines) : 0;

  return ok(
    { lines, words, chars, byteSize, avgLineLength },
    'OK',
    'Document statistics computed',
  );
}

// ═══ §7 DOCUMENT COMPARISON — dirty check, change detection, checksum ═══

export function isDocumentDirty(state: DocumentState): boolean {
  return state.isDirty;
}

export function hasChanges(original: string, current: string): boolean {
  return original !== current;
}

export function computeChecksum(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const ch = content.charCodeAt(i);
    hash = ((hash << 5) - hash + ch) | 0;
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// ═══ INTERNAL HELPERS ═══

function detectLineEnding(content: string): LineEnding {
  const crlfCount = (content.match(/\r\n/g) || []).length;
  const lfCount = (content.match(/(?<!\r)\n/g) || []).length;
  const crCount = (content.match(/\r(?!\n)/g) || []).length;

  if (crlfCount >= lfCount && crlfCount >= crCount && crlfCount > 0) return 'crlf';
  if (crCount > lfCount && crCount > 0) return 'cr';
  return 'lf';
}

function normalizeLineEndings(content: string, target: LineEnding): string {
  const canonical = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (target === 'lf') return canonical;
  if (target === 'crlf') return canonical.replace(/\n/g, '\r\n');
  return canonical.replace(/\n/g, '\r');
}

function splitLines(content: string): string[] {
  return content.split(/\r\n|\r|\n/);
}

function joinLines(lines: string[], eol: string): string {
  return lines.join(eol);
}

function updateDocumentContent(state: DocumentState, newContent: string): DocumentState {
  return {
    ...state,
    content: newContent,
    version: state.version + 1,
    lineCount: lineCount(newContent),
    charCount: newContent.length,
    isDirty: true,
  };
}
